import { useAuth } from 'utils/auth'
import { deviceType } from 'utils/deviceType'
import { useSatuEventTracker } from 'utils/satuEvent'
import AndroidHandler from 'utils/androidHandler'
import { APP_DEVICE_ID_STORAGE_KEY } from 'configs/android'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

import type { IInfoterkiniCategory } from 'types/info-terkini'

const SATU_EVENT_NAME = 'guru_core_pwa_event_tracker_v1'
const TRACKER_EVENT_NAME = 'GURU_CORE_APP'
const TRACKER_SCREEN_NAME = 'HOMEPAGE'
const DEFAULT_TIMEOUT = 5000 // 5 seconds

type TSETrackEventDefaultAtributes = {
  screen_name: string
  is_login: [boolean, 'bool']
  is_webview: [boolean, 'bool']
  device_id?: string
}

export function useSETrackerEvent() {
  const [, , , getAppDeviceIdValue] = useLocalStorage(
    APP_DEVICE_ID_STORAGE_KEY,
    null
  )

  const appDeviceID = getAppDeviceIdValue()
  const hasAppDeviceID = Boolean(appDeviceID)
  const androidHandler = AndroidHandler()
  const { session } = useAuth()
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()

  const SETrackEventDefaultAtributes: TSETrackEventDefaultAtributes = {
    screen_name: TRACKER_SCREEN_NAME,
    is_login: [Boolean(session), 'bool'],
    is_webview: [androidHandler.enabled, 'bool'],
  }

  const SETrackComponent = (
    activity_type: string,
    additionalAttributes?: Record<string, any>
  ): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      eventParams: {
        activity_type,
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAtributes,
          event_trigger: TRACKER_SCREEN_NAME,
          ...additionalAttributes,
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  function SETrackInfoTerkiniFilterButtonClicked() {
    SETrackComponent('CLICK_FILTER_BUTTON', {
      event_trigger: 'INFORMATION_PAGE',
      screen_name: 'INFORMATION_PAGE',
      page_name: window.location.href,
      user_email: session?.user?.email ?? null,
      device: deviceType(),
      page_element: null,
    })
  }

  function SETrackInfoTerkiniChipClicked(chips: IInfoterkiniCategory[]) {
    SETrackComponent('FILTER_CHIPS_CLICK', {
      event_trigger: 'INFORMATION_PAGE',
      screen_name: 'INFORMATION_PAGE',
      page_name: window.location.href,
      user_email: session?.user?.email ?? null,
      device: deviceType(),
      page_element: JSON.stringify({
        chips,
      }),
    })
  }

  function SETrackOpenInformationPage(eventTrigger: string) {
    SETrackComponent('OPEN_INFORMATION_PAGE', {
      event_trigger: eventTrigger,
      screen_name: 'INFORMATION_PAGE',
      page_name: `${window.location.origin}/info-terkini`,
      user_email: session?.user?.email ?? null,
      device: deviceType(),
      page_element: null,
    })
  }

  return {
    SETrackInfoTerkiniFilterButtonClicked,
    SETrackInfoTerkiniChipClicked,
    SETrackOpenInformationPage,
  }
}
